import { Injectable } from '@angular/core';
import { StateToken, State, Action, StateContext, Selector } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { patch } from '@ngxs/store/operators';
import { ApiResponse, ApiResponseWithLocations } from 'src/app/core/models/api-response';
import { DueListApiService } from '../services/due-list-api.service';
import { IDueListItem } from '../models/due-list-item.model';
import {
  LoadDueListCounts,
  LoadDueListDueCompetences,
  LoadDueListItems,
  LoadDueListWarnings,
  LoadMyDueListItems,
  LoadMyDueListWarnings,
  PrintDueListWarnings,
} from './due-list.actions';
import { IDueListCounts } from '../models/due-list-counts.model';

export interface DueListStateModel {
  warnings: IDueListItem[];
  dueCompetences: IDueListItem[];
  locations: string[];
  summaryOfWarnings: number;
  currentPrintedDueListWarnings: Blob | undefined;
  warningsTotalPages: number;
  myWarningsTotalPages: number;
  competencesTotalPages: number;
  dueListCounts: IDueListCounts | undefined;
}

const DUE_LIST_STATE_TOKEN = new StateToken<DueListStateModel>('dueList');

@State<DueListStateModel>({
  name: DUE_LIST_STATE_TOKEN,
  defaults: {
    warnings: [],
    dueCompetences: [],
    locations: [],
    summaryOfWarnings: 0,
    currentPrintedDueListWarnings: undefined,
    warningsTotalPages: 0,
    myWarningsTotalPages: 0,
    competencesTotalPages: 0,
    dueListCounts: undefined,
  },
})
@Injectable()
export class DueListState {
  constructor(private service: DueListApiService) {}

  @Action(LoadDueListItems, { cancelUncompleted: true })
  loadDueListItems(ctx: StateContext<DueListStateModel>) {
    ctx.dispatch([
      new LoadDueListWarnings(),
      new LoadDueListDueCompetences(),
      new LoadDueListCounts(),
    ]);
  }

  @Action(LoadMyDueListItems, { cancelUncompleted: true })
  loadMyDueListItems(ctx: StateContext<DueListStateModel>) {
    ctx.dispatch([
      new LoadMyDueListWarnings(),
      new LoadDueListDueCompetences(),
      new LoadDueListCounts(),
    ]);
  }

  @Action(LoadDueListWarnings, { cancelUncompleted: true })
  loadDueListWarnings(ctx: StateContext<DueListStateModel>, { filter }: LoadDueListWarnings) {
    return this.service.getDueListWarnings(filter).pipe(
      tap((response: ApiResponseWithLocations<IDueListItem[]>) => {
        if (response) {
          ctx.setState(
            patch({
              warnings: response.data,
              warningsTotalPages: response.totalPages,
              locations: response.locations,
            })
          );
        }
      })
    );
  }

  @Action(LoadDueListDueCompetences, { cancelUncompleted: true })
  loadDueListDueCompetences(
    ctx: StateContext<DueListStateModel>,
    { filter }: LoadDueListDueCompetences
  ) {
    return this.service.getDueListDueCompetences(filter).pipe(
      tap((response: ApiResponse<IDueListItem[]>) => {
        if (response) {
          ctx.setState(
            patch({ dueCompetences: response.data, competencesTotalPages: response.totalPages })
          );
        }
      })
    );
  }

  @Action(LoadMyDueListWarnings, { cancelUncompleted: true })
  loadMyDueListWarnings(ctx: StateContext<DueListStateModel>, { filter }: LoadMyDueListWarnings) {
    return this.service.getMyDueListWarnings(filter).pipe(
      tap((response: ApiResponseWithLocations<IDueListItem[]>) => {
        if (response) {
          ctx.setState(
            patch({
              warnings: response.data,
              myWarningsTotalPages: response.totalPages,
              locations: response.locations,
            })
          );
        }
      })
    );
  }

  @Action(PrintDueListWarnings, { cancelUncompleted: true })
  printDueListWarnings(
    { setState }: StateContext<DueListStateModel>,
    { request }: PrintDueListWarnings
  ) {
    return this.service.print(request).pipe(
      tap((file) => {
        setState(patch({ currentPrintedDueListWarnings: file }));
      })
    );
  }

  @Action(LoadDueListCounts, { cancelUncompleted: true })
  loadDueListCounts(ctx: StateContext<DueListStateModel>) {
    return this.service.getDueListCounts().pipe(
      tap((response: IDueListCounts) => {
        if (response) {
          ctx.setState(patch({ dueListCounts: response }));
        }
      })
    );
  }

  @Selector()
  static getDueListSummaryNumber(state: DueListStateModel): number {
    const warningsAmount = state.warnings?.length;
    const dueCompetencesAmount = state.dueCompetences?.length;
    const summary = warningsAmount + dueCompetencesAmount;
    if (summary > 0) {
      return summary;
    }
    return state.summaryOfWarnings;
  }

  @Selector()
  static getWarnings(state: DueListStateModel): IDueListItem[] {
    return state.warnings;
  }

  @Selector()
  static getTotalPagesForWarnings(state: DueListStateModel): number {
    return state.warningsTotalPages;
  }

  @Selector()
  static getLocations(state: DueListStateModel): string[] {
    return state.locations;
  }

  @Selector()
  static getTotalPagesForCompetences(state: DueListStateModel): number {
    return state.competencesTotalPages;
  }

  @Selector()
  static getTotalPagesForMyWarnings(state: DueListStateModel): number {
    return state.myWarningsTotalPages;
  }

  @Selector()
  static getDueCompetences(state: DueListStateModel): IDueListItem[] {
    return state.dueCompetences;
  }

  @Selector()
  static getCurrentPrintedDueListWarnings(state: DueListStateModel) {
    return state.currentPrintedDueListWarnings;
  }

  @Selector()
  static getDueListCounts(state: DueListStateModel): IDueListCounts | undefined {
    return state.dueListCounts;
  }
}
